// Fichier: frontend/src/services/apiService.js

// L'URL de base de votre API XAMPP (corrigée selon votre structure)
const API_BASE_URL =
  process.env.VUE_APP_API_BASE_URL ||
  "http://localhost/MeetingHubApp/backend/api/";

// Helper pour gérer les réponses HTTP (vérifie si ok, parse JSON, gère les erreurs API)
async function handleResponse(response) {
  if (!response.ok) {
    // Essayer de lire le message d'erreur JSON du backend si disponible
    const errorData = await response
      .json()
      .catch(() => ({ error: response.statusText }));
    const errorMessage =
      errorData.error || `HTTP error! status: ${response.status}`;
    throw new Error(errorMessage);
  }
  // Gérer les réponses 204 No Content (souvent pour DELETE) ou les réponses vides
  if (
    response.status === 204 ||
    response.headers.get("Content-Length") === "0"
  ) {
    return null; // Pas de contenu à retourner
  }
  // Sinon, parser le JSON
  return response.json();
}

const apiService = {
  // Récupérer toutes les réunions
  async getMeetings() {
    try {
      const response = await fetch(`${API_BASE_URL}meetings.php`);
      const data = await handleResponse(response);
      // L'API meetings.php ne renvoie pas les actions liées pour chaque réunion dans la liste
      // On pourrait modifier le backend pour inclure un count ou les actions si besoin,
      // ou les récupérer séparément ici si nécessaire pour l'affichage dans la carte.
      // Pour l'instant, MeetingCard affiche un compte basique (souvent 0)
      return data;
    } catch (error) {
      console.error("Error fetching meetings:", error);
      throw error;
    }
  },

  // Récupérer une réunion spécifique par ID (inclut ses actions)
  async getMeeting(id) {
    try {
      const response = await fetch(`${API_BASE_URL}meeting.php?id=${id}`);
      const data = await handleResponse(response);
      // S'assurer que les actions sont bien un tableau (API PHP le fait, mais bonne pratique)
      if (data && data.actions && !Array.isArray(data.actions)) {
        data.actions = [];
      } else if (data && !data.actions) {
        data.actions = [];
      }
      return data;
    } catch (error) {
      console.error(`Error fetching meeting ${id}:`, error);
      throw error;
    }
  },

  // Créer une nouvelle réunion
  async createMeeting(meetingData) {
    try {
      const response = await fetch(`${API_BASE_URL}meetings.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(meetingData),
      });
      const newMeeting = await handleResponse(response);
      return newMeeting; // Devrait retourner l'objet réunion complet avec ID
    } catch (error) {
      console.error("Error creating meeting:", error);
      throw error;
    }
  },

  // ** NOUVEAU : Mettre à jour une réunion par ID **
  async updateMeeting(id, updateData) {
    try {
      const response = await fetch(`${API_BASE_URL}meeting.php?id=${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData), // updateData peut contenir title, meeting_date, etc.
      });
      const updatedMeeting = await handleResponse(response);
      return updatedMeeting; // Devrait retourner l'objet réunion mis à jour ou un message
    } catch (error) {
      console.error(`Error updating meeting ${id}:`, error);
      throw error;
    }
  },

  // ** NOUVEAU : Supprimer une réunion par ID **
  async deleteMeeting(id) {
    try {
      const response = await fetch(`${API_BASE_URL}meeting.php?id=${id}`, {
        method: "DELETE",
      });
      // DELETE peut retourner un message ou être No Content (204)
      const result = await handleResponse(response);
      return result || { message: `Réunion ID ${id} supprimée.` }; // Retourne un message même si réponse vide
    } catch (error) {
      console.error(`Error deleting meeting ${id}:`, error);
      throw error;
    }
  },

  // Récupérer toutes les actions (peut être filtré par meeting_id)
  async getActions(meetingId = null) {
    try {
      const url = meetingId
        ? `${API_BASE_URL}actions.php?meeting_id=${meetingId}`
        : `${API_BASE_URL}actions.php`;
      const response = await fetch(url);
      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error("Error fetching actions:", error);
      throw error;
    }
  },

  // Créer une nouvelle action pour une réunion
  async createAction(actionData) {
    try {
      const response = await fetch(`${API_BASE_URL}actions.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(actionData), // actionData doit inclure meeting_id, description, etc.
      });
      const newAction = await handleResponse(response);
      return newAction; // Devrait retourner l'objet action complet avec ID
    } catch (error) {
      console.error("Error creating action:", error);
      throw error;
    }
  },

  // Mettre à jour une action par ID (utilisé notamment pour changer le statut)
  async updateAction(id, updateData) {
    try {
      const response = await fetch(`${API_BASE_URL}action.php?id=${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData), // updateData peut contenir { status: 'done' }, { description: '...' } etc.
      });
      const updatedAction = await handleResponse(response);
      return updatedAction; // Devrait retourner l'objet mis à jour ou un message
    } catch (error) {
      console.error(`Error updating action ${id}:`, error);
      throw error;
    }
  },

  // ** NOUVEAU : Supprimer une action par ID **
  async deleteAction(id) {
    try {
      const response = await fetch(`${API_BASE_URL}action.php?id=${id}`, {
        method: "DELETE",
      });
      const result = await handleResponse(response);
      return result || { message: `Action ID ${id} supprimée.` }; // Retourne un message même si réponse vide
    } catch (error) {
      console.error(`Error deleting action ${id}:`, error);
      throw error;
    }
  },
};

export default apiService;
